import React, {
  useEffect, useState, useCallback, useRef, RefObject, ReactElement,
} from 'react';
import FormatService from '../../services/format-service';
import useDragElement from '../../hooks/use-drag-element';
import useDropElement from '../../hooks/use-drop-element';
import useStylesDraggableItem from './draggable-item.styles';
import { HTML_TAG_NAME_DIV } from '../../constants/system/html-tag-names';

const DraggableItem: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isHovered,
    isDisabled,
    isHoverInverted,
    withHighlight,
    elementType,
    itemId,
    index,
    dropDeps,
    onDropItem,
    onHoverItem,
    onReleaseItem,
    children,
  } = props;

  const { classes } = useStylesDraggableItem();
  const draggableItemRef: RefObject<HTMLDivElement> | null = useRef(null);

  const activeElementType: string = elementType || HTML_TAG_NAME_DIV;

  const [isInitialized, setIsInitialized]: [boolean, Function] = useState(false);

  const dropItem: Function = useCallback(async (item: DropProps): Promise<void> => {
    const resultItem: DropItem = item;

    if (!draggableItemRef.current) {
      return;
    }

    if (onReleaseItem) {
      onReleaseItem();
    }

    const dragIndex: number = resultItem.index;
    const hoverIndex: number = index;

    if (dragIndex === hoverIndex) {
      return;
    }

    if (onDropItem) {
      onDropItem(dragIndex, hoverIndex);
    }

    resultItem.index = hoverIndex;
  }, [draggableItemRef, index, ...[dropDeps || []]]);

  const {
    isDragging,
    dragRef,
  } = useDragElement(activeElementType, itemId, index);

  const {
    dropRef,
  } = useDropElement(activeElementType, index, isDisabled, dropItem, onHoverItem);

  const disabledClassName: string | undefined = isDisabled ? classes.draggableItemDisabled : undefined;
  const draggingClassName: string | undefined = isDragging ? classes.draggableItemDragging : undefined;
  const hoveredClassName: string | undefined = isHovered && withHighlight && !isHoverInverted
    ? classes.draggableItemHovered : undefined;
  const hoveredInvertedClassName: string | undefined = isHovered && withHighlight && isHoverInverted
    ? classes.draggableItemHoveredInverted : undefined;

  useEffect((): void => {
    if (!isInitialized && !isDisabled) {
      dragRef(dropRef(draggableItemRef));
      setIsInitialized(true);
    }

    if (isDisabled) {
      setIsInitialized(false);
      dragRef(dropRef(null));
    }
  }, [draggableItemRef, isDisabled]);

  return (
    <div
      ref={draggableItemRef}
      className={FormatService.formatClassNames(
        classes.draggableItem, disabledClassName, draggingClassName, hoveredClassName, hoveredInvertedClassName,
      )}
    >
      {children}
    </div>
  );
});

export default DraggableItem;
